import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { 
  ArrowRight, 
  BarChart3, 
  FileText, 
  Package, 
  Zap, 
  Shield, 
  RefreshCcw,
  Check,
  Menu,
  X
} from 'lucide-react';
import { useState } from 'react';
import { motion } from 'framer-motion';

const features = [
  {
    icon: Package,
    title: "Gestion des Commandes",
    description: "Suivez toutes vos commandes Back Market en temps réel avec un tableau de bord centralisé."
  },
  {
    icon: BarChart3,
    title: "Analytics Avancés",
    description: "Visualisez vos performances, revenus et tendances avec des graphiques interactifs."
  },
  {
    icon: FileText,
    title: "Facturation Automatique",
    description: "Générez et envoyez vos factures automatiquement vers Pennylane en un clic."
  },
  {
    icon: RefreshCcw,
    title: "Synchronisation Temps Réel",
    description: "Vos données sont synchronisées en continu avec Back Market et Pennylane."
  },
  {
    icon: Zap,
    title: "Gestion des Prix",
    description: "Modifiez vos prix et stocks directement depuis notre interface intuitive."
  },
  {
    icon: Shield,
    title: "Sécurité Maximale",
    description: "Vos tokens API sont chiffrés et stockés de manière sécurisée."
  }
];

const pricingPlans = [
  {
    name: "Starter",
    price: "29",
    description: "Parfait pour démarrer",
    features: ["100 commandes/mois", "1 intégration", "Support email", "Dashboard basique"]
  },
  {
    name: "Pro",
    price: "79",
    popular: true,
    description: "Le plus populaire",
    features: ["Commandes illimitées", "Toutes intégrations", "Support prioritaire", "API avancée", "Export de données"]
  },
  {
    name: "Enterprise",
    price: "199",
    description: "Pour les grandes équipes",
    features: ["Tout Pro", "Multi-comptes", "Support dédié", "SLA garanti", "Formation personnalisée"]
  }
];

export default function LandingPage() {
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                <RefreshCcw className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                BackMarket<span className="text-emerald-500">Pro</span>
              </span>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors">Fonctionnalités</a>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 transition-colors">Tarifs</a>
              {user ? (
                <Link to="/dashboard">
                  <Button className="bg-emerald-500 hover:bg-emerald-600">
                    Dashboard
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              ) : (
                <div className="flex items-center gap-3">
                  <Link to="/login">
                    <Button variant="ghost">Connexion</Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-emerald-500 hover:bg-emerald-600">
                      Commencer
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 py-4 px-4 space-y-4">
            <a href="#features" className="block text-slate-600 hover:text-slate-900">Fonctionnalités</a>
            <a href="#pricing" className="block text-slate-600 hover:text-slate-900">Tarifs</a>
            {user ? (
              <Link to="/dashboard">
                <Button className="w-full bg-emerald-500 hover:bg-emerald-600">Dashboard</Button>
              </Link>
            ) : (
              <div className="space-y-2">
                <Link to="/login" className="block">
                  <Button variant="outline" className="w-full">Connexion</Button>
                </Link>
                <Link to="/register" className="block">
                  <Button className="w-full bg-emerald-500 hover:bg-emerald-600">Commencer</Button>
                </Link>
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-8"
            >
              <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-200 border-0">
                Nouveau : Intégration Pennylane
              </Badge>
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Gérez votre compte
                <span className="text-emerald-500"> Back Market </span>
                comme un pro
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-lg">
                Centralisez vos commandes, automatisez vos factures et optimisez vos prix. 
                Le SaaS conçu pour les vendeurs professionnels de produits reconditionnés.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button 
                    size="lg" 
                    className="bg-emerald-500 hover:bg-emerald-600 text-lg px-8 py-6 shadow-lg shadow-emerald-500/20"
                    data-testid="hero-cta-btn"
                  >
                    Démarrer gratuitement
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="#features">
                  <Button variant="outline" size="lg" className="text-lg px-8 py-6">
                    En savoir plus
                  </Button>
                </a>
              </div>
              <div className="flex items-center gap-6 pt-4">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full bg-slate-200 border-2 border-white" />
                  ))}
                </div>
                <p className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-900">500+</span> vendeurs nous font confiance
                </p>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/20 to-lime-300/20 rounded-3xl blur-3xl" />
              <Card className="relative bg-white/80 backdrop-blur border-slate-200 shadow-2xl">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-400" />
                      <div className="w-3 h-3 rounded-full bg-yellow-400" />
                      <div className="w-3 h-3 rounded-full bg-emerald-400" />
                    </div>
                    <Badge variant="outline" className="text-xs">Dashboard Preview</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-emerald-600">127</p>
                      <p className="text-xs text-slate-500">Commandes</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-slate-900">€12.4k</p>
                      <p className="text-xs text-slate-500">Revenus</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-emerald-600">98%</p>
                      <p className="text-xs text-slate-500">Satisfaction</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                        <div className="w-10 h-10 rounded bg-slate-200" />
                        <div className="flex-1">
                          <div className="h-3 bg-slate-200 rounded w-3/4 mb-1" />
                          <div className="h-2 bg-slate-100 rounded w-1/2" />
                        </div>
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">Expédié</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-emerald-100 text-emerald-800 mb-4">Fonctionnalités</Badge>
            <h2 
              className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Tout ce dont vous avez besoin
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Une suite complète d'outils pour gérer votre activité Back Market efficacement.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow border-slate-200 hover:border-emerald-200">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-emerald-600" />
                    </div>
                    <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-slate-600">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-emerald-100 text-emerald-800 mb-4">Tarification</Badge>
            <h2 
              className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Des tarifs adaptés à votre activité
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Choisissez le plan qui correspond à vos besoins. Évoluez à tout moment.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className={`h-full relative ${plan.popular ? 'border-emerald-500 border-2 shadow-xl' : 'border-slate-200'}`}>
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-emerald-500 text-white">Le plus populaire</Badge>
                    </div>
                  )}
                  <CardHeader className="text-center pb-2">
                    <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{plan.name}</CardTitle>
                    <CardDescription>{plan.description}</CardDescription>
                    <div className="pt-4">
                      <span className="text-4xl font-bold text-slate-900">{plan.price}€</span>
                      <span className="text-slate-500">/mois</span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ul className="space-y-3">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-slate-600">
                          <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to="/register" className="block">
                      <Button 
                        className={`w-full ${plan.popular ? 'bg-emerald-500 hover:bg-emerald-600' : ''}`}
                        variant={plan.popular ? 'default' : 'outline'}
                        data-testid={`pricing-${plan.name.toLowerCase()}-btn`}
                      >
                        Choisir {plan.name}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-emerald-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-3xl sm:text-4xl font-bold text-white mb-6"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Prêt à optimiser votre activité Back Market ?
          </h2>
          <p className="text-lg text-emerald-100 mb-8 max-w-2xl mx-auto">
            Rejoignez des centaines de vendeurs qui ont déjà transformé leur gestion quotidienne.
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              className="bg-white text-emerald-600 hover:bg-emerald-50 text-lg px-8 py-6"
              data-testid="cta-start-btn"
            >
              Commencer maintenant
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                <RefreshCcw className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-lg text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                BackMarketPro
              </span>
            </div>
            <p className="text-sm">
              © 2024 BackMarketPro. Tous droits réservés. | Eco Mobile Solution
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
